import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { WrapBand } from '@/lib/text/types/wrap-band'
import type { Point } from './types/point'
import { getCanvasContourPolygon } from './helpers/contour-polygon'
import { inflatedIntervalsForSlice } from './helpers/scanline-intervals'

/** Target height of each contour scanline slice (pt) — bounds how closely the wrap boundary
 *  follows curved/angled edges vertically. */
const CONTOUR_SLICE_HEIGHT_PT = 6
/** Hard cap on slices per shape so a very tall shape still costs a bounded amount of work;
 *  slice height is coarsened (not slice count reduced below shape coverage) when hit. */
const MAX_CONTOUR_SLICES = 200

/**
 * Slices `canvasPolygon` (any coordinate space — canvas, or already text-box-local; only its
 * position relative to `contentX/contentY` matters) into `WrapBand`s via horizontal scanline
 * strips, inflated uniformly by `offset` (disk-Minkowski-sum — see `inflatedIntervalsForSlice`).
 * The shared engine behind BOTH contour-based wrap styles: `computeShapeContourBands` below feeds
 * it a shape's real outline polygon for Wrap Shape's single uniform "Offset" field, and
 * `computeRotatedBoundingBoxBands` (`compute-wrap-obstacles.ts`) feeds it an already
 * per-side-offset-expanded, rotated rectangle (4 corners, `offset: 0` since the offsets are baked
 * into the polygon's own vertices) for Bounding Box — both need the identical "does this row's
 * slice intersect the TRUE polygon, not just its AABB" scanline logic; only the input polygon and
 * whether extra uniform inflation still applies differ.
 */
export function bandsFromCanvasPolygon(
  canvasPolygon: Point[],
  offset: number,
  contentX: number,
  contentY: number,
  contentWidth: number,
  contentHeight: number
): WrapBand[] {
  if (contentWidth <= 0 || contentHeight <= 0 || canvasPolygon.length < 3) return []

  const polygon = canvasPolygon.map((p) => ({ x: p.x - contentX, y: p.y - contentY }))
  const safeOffset = Number.isFinite(offset) ? Math.max(0, offset) : 0

  let minY = Infinity
  let maxY = -Infinity

  for (const p of polygon) {
    minY = Math.min(minY, p.y)
    maxY = Math.max(maxY, p.y)
  }

  if (!Number.isFinite(minY) || !Number.isFinite(maxY)) return []

  const expandedTop = minY - safeOffset
  const expandedBottom = maxY + safeOffset
  const totalHeight = expandedBottom - expandedTop

  if (totalHeight <= 0) return []

  const sliceCount = Math.min(
    MAX_CONTOUR_SLICES,
    Math.max(1, Math.ceil(totalHeight / CONTOUR_SLICE_HEIGHT_PT))
  )
  const sliceHeight = totalHeight / sliceCount
  const bands: WrapBand[] = []

  for (let i = 0; i < sliceCount; i++) {
    const sliceTop = expandedTop + i * sliceHeight
    const sliceBottom = sliceTop + sliceHeight
    const intervals = inflatedIntervalsForSlice(polygon, sliceTop, sliceBottom, safeOffset)

    for (const [x0, x1] of intervals) {
      const left = Math.max(0, x0)
      const right = Math.min(contentWidth, x1)
      const top = Math.max(0, sliceTop)
      const bottom = Math.min(contentHeight, sliceBottom)

      if (right <= left || bottom <= top) continue
      bands.push({ top, bottom, left, right })
    }
  }

  return bands
}

/**
 * Contour-based exclusion bands for one `wrapStyle: 'wrap'` shape, in the same text-box-local
 * content coordinate space `WrapBand` uses (see `compute-wrap-obstacles.ts`). `offset` (AC 2.2's
 * single "Offset" value, stored in `wrapOffsetTop`) expands the contour uniformly in every
 * direction — `offset === 0` hugs the exact outline.
 */
export function computeShapeContourBands(
  shape: ShapeData,
  offset: number,
  contentX: number,
  contentY: number,
  contentWidth: number,
  contentHeight: number
): WrapBand[] {
  return bandsFromCanvasPolygon(
    getCanvasContourPolygon(shape),
    offset,
    contentX,
    contentY,
    contentWidth,
    contentHeight
  )
}
